'use client';

import React, { useEffect, useRef, useState } from 'react';
import useAuthStore from '@/store/auth';
import { isWebGLSupported } from '@/utils/hardwareDetection';
import { Map, Navigation, Compass, Layers, Check, ExternalLink, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

interface SimpleRouteMapProps {
  routePoints: [number, number][]; // Array of [lat, lon]
  vesselName?: string;
  startLabel?: string;
  endLabel?: string;
  startDate?: string;
  endDate?: string;
  className?: string;
  height?: string;
}

const MAPLIBRE_JS_URL = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.js';
const MAPLIBRE_CSS_URL = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.css';

function loadMapLibreScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return resolve();
    if ((window as any).maplibregl) {
      resolve();
      return;
    }
    const existing = document.getElementById('maplibre-gl-js');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    if (!document.getElementById('maplibre-gl-css')) {
      const link = document.createElement('link');
      link.id = 'maplibre-gl-css';
      link.rel = 'stylesheet';
      link.href = MAPLIBRE_CSS_URL;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.id = 'maplibre-gl-js';
    script.src = MAPLIBRE_JS_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function SimpleRouteMap({
  routePoints,
  vesselName,
  startLabel,
  endLabel,
  startDate,
  endDate,
  className,
  height = '500px',
}: SimpleRouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Ambil konfigurasi preferensi engine map user (MapGL vs Leaflet)
  const mapMode = useAuthStore((state) => state.mapMode);
  const useWebGL = useAuthStore((state) => state.useWebGL);
  const isHardwareGL = typeof window !== 'undefined' ? isWebGLSupported() : true;
  const preferredEngine: 'mapgl' | 'leaflet' =
    (mapMode ? mapMode === 'maps-gl' : useWebGL) && isHardwareGL ? 'mapgl' : 'leaflet';

  // State engine aktif (default dari preferensi user, namun user dapat beralih via tombol jika diinginkan)
  const [activeEngine, setActiveEngine] = useState<'mapgl' | 'leaflet'>(preferredEngine);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Filter titik yang valid
  const validPoints = (routePoints || []).filter(
    (pt) => Array.isArray(pt) && pt.length >= 2 && !isNaN(pt[0]) && !isNaN(pt[1]) && (pt[0] !== 0 || pt[1] !== 0),
  );

  useEffect(() => {
    if (!containerRef.current || validPoints.length < 2) return;

    let isCancelled = false;
    let leafletMap: any = null;
    let glMap: any = null;
    let resizeObserver: ResizeObserver | null = null;

    setMapLoaded(false);

    // Kosongkan container sebelum inisialisasi ulang
    containerRef.current.innerHTML = '';

    const firstPoint = validPoints[0]; // [lat, lon]
    const lastPoint = validPoints[validPoints.length - 1]; // [lat, lon]

    if (activeEngine === 'mapgl') {
      // ── MAPLIBRE GL JS ENGINE ──
      loadMapLibreScript()
        .then(() => {
          if (isCancelled || !containerRef.current) return;
          const maplibregl = (window as any).maplibregl;
          if (!maplibregl) {
            console.warn('[SimpleRouteMap] maplibregl not found, falling back to leaflet');
            setActiveEngine('leaflet');
            return;
          }

          // GeoJSON Line Coordinates: [lon, lat]
          const glCoordinates = validPoints.map(([lat, lon]) => [lon, lat]);

          // Hitung bounding box
          let minLon = glCoordinates[0][0];
          let maxLon = glCoordinates[0][0];
          let minLat = glCoordinates[0][1];
          let maxLat = glCoordinates[0][1];

          glCoordinates.forEach(([lon, lat]) => {
            if (lon < minLon) minLon = lon;
            if (lon > maxLon) maxLon = lon;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
          });

          const map = new maplibregl.Map({
            container: containerRef.current,
            style: {
              version: 8,
              sources: {
                'google-tiles': {
                  type: 'raster',
                  tiles: ['https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'],
                  tileSize: 256,
                },
              },
              layers: [
                {
                  id: 'google-tiles-layer',
                  type: 'raster',
                  source: 'google-tiles',
                  minzoom: 0,
                  maxzoom: 20,
                },
              ],
            },
            center: [(minLon + maxLon) / 2, (minLat + maxLat) / 2],
            zoom: 6,
            attributionControl: false,
          });

          glMap = map;

          map.on('load', () => {
            if (isCancelled) return;

            // 1. Source Line
            map.addSource('route-source', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: glCoordinates,
                },
              },
            });

            // 2. Line Shadow/Glow Layer
            map.addLayer({
              id: 'route-glow',
              type: 'line',
              source: 'route-source',
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': '#1d4ed8',
                'line-width': 6,
                'line-opacity': 0.4,
              },
            });

            // 3. Line Main Layer (Garis Biru Solid dengan Aksen)
            map.addLayer({
              id: 'route-line',
              type: 'line',
              source: 'route-source',
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': '#3b82f6',
                'line-width': 3.5,
              },
            });

            // 4. Marker START (Hijau)
            const startEl = document.createElement('div');
            startEl.className = 'route-start-marker';
            startEl.innerHTML = `
              <div style="background:#10b981;color:#fff;padding:2px 7px;border-radius:12px;font-size:10px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;gap:3px;transform:translate(-50%,-100%);white-space:nowrap;">
                <span>▶</span>
                <span>START${startLabel ? ` · ${startLabel}` : ''}</span>
              </div>
            `;
            new maplibregl.Marker({ element: startEl })
              .setLngLat([firstPoint[1], firstPoint[0]])
              .setPopup(
                new maplibregl.Popup({ offset: [0, -15] }).setHTML(
                  `<div style="font-size:11px;padding:3px;"><strong>Keberangkatan:</strong><br/>${startLabel || 'Titik Awal'}<br/><span style="color:#6b7280;">${startDate || ''}</span></div>`,
                ),
              )
              .addTo(map);

            // 5. Marker END (Merah)
            const endEl = document.createElement('div');
            endEl.className = 'route-end-marker';
            endEl.innerHTML = `
              <div style="background:#ef4444;color:#fff;padding:2px 7px;border-radius:12px;font-size:10px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;gap:3px;transform:translate(-50%,-100%);white-space:nowrap;">
                <span>⏹</span>
                <span>END${endLabel ? ` · ${endLabel}` : ''}</span>
              </div>
            `;
            new maplibregl.Marker({ element: endEl })
              .setLngLat([lastPoint[1], lastPoint[0]])
              .setPopup(
                new maplibregl.Popup({ offset: [0, -15] }).setHTML(
                  `<div style="font-size:11px;padding:3px;"><strong>Kedatangan / Posisi Akhir:</strong><br/>${endLabel || 'Titik Tujuan'}<br/><span style="color:#6b7280;">${endDate || ''}</span></div>`,
                ),
              )
              .addTo(map);

            // Fit bounds
            map.fitBounds(
              [
                [minLon, minLat],
                [maxLon, maxLat],
              ],
              {
                padding: { top: 40, bottom: 40, left: 40, right: 40 },
                maxZoom: 14,
                duration: 600,
              },
            );

            setMapLoaded(true);
          });

          // Resize observer
          resizeObserver = new ResizeObserver(() => {
            if (map) {
              map.resize();
            }
          });
          resizeObserver.observe(containerRef.current);
        })
        .catch((err) => {
          console.error('[SimpleRouteMap] Error loading MapLibre:', err);
          setActiveEngine('leaflet');
        });
    } else {
      // ── LEAFLET ENGINE ──
      (async () => {
        const L = (await import('leaflet')).default;
        if (isCancelled || !containerRef.current) return;

        const map = L.map(containerRef.current, {
          center: [firstPoint[0], firstPoint[1]],
          zoom: 7,
          zoomControl: true,
          attributionControl: false,
        });

        leafletMap = map;

        // Tile Google Maps
        L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          maxZoom: 20,
        }).addTo(map);

        // Polyline Rute
        const latLngs = validPoints.map(([lat, lon]) => [lat, lon]);
        const polyline = L.polyline(latLngs, {
          color: '#2563eb',
          weight: 4,
          opacity: 0.9,
          dashArray: '6, 6',
          lineJoin: 'round',
        }).addTo(map);

        // Marker START (Hijau)
        const startIcon = L.divIcon({
          className: 'leaflet-route-start',
          html: `
            <div style="background:#10b981;color:#fff;padding:2px 7px;border-radius:12px;font-size:10px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:inline-flex;align-items:center;gap:3px;white-space:nowrap;transform:translate(-50%,-100%);">
              <span>▶</span>
              <span>START${startLabel ? ` · ${startLabel}` : ''}</span>
            </div>
          `,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        });

        L.marker([firstPoint[0], firstPoint[1]], { icon: startIcon })
          .bindPopup(
            `<div style="font-size:11px;"><strong>Keberangkatan:</strong><br/>${startLabel || 'Titik Awal'}<br/><span style="color:#6b7280;">${startDate || ''}</span></div>`,
          )
          .addTo(map);

        // Marker END (Merah)
        const endIcon = L.divIcon({
          className: 'leaflet-route-end',
          html: `
            <div style="background:#ef4444;color:#fff;padding:2px 7px;border-radius:12px;font-size:10px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:inline-flex;align-items:center;gap:3px;white-space:nowrap;transform:translate(-50%,-100%);">
              <span>⏹</span>
              <span>END${endLabel ? ` · ${endLabel}` : ''}</span>
            </div>
          `,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        });

        L.marker([lastPoint[0], lastPoint[1]], { icon: endIcon })
          .bindPopup(
            `<div style="font-size:11px;"><strong>Kedatangan / Posisi Akhir:</strong><br/>${endLabel || 'Titik Tujuan'}<br/><span style="color:#6b7280;">${endDate || ''}</span></div>`,
          )
          .addTo(map);

        // Fit bounds
        try {
          map.fitBounds(polyline.getBounds(), { padding: [40, 40] });
        } catch (e) {}

        // Resize observer
        resizeObserver = new ResizeObserver(() => {
          if (map) {
            map.invalidateSize();
          }
        });
        resizeObserver.observe(containerRef.current);

        setMapLoaded(true);
      })();
    }

    return () => {
      isCancelled = true;
      if (resizeObserver && containerRef.current) {
        resizeObserver.disconnect();
      }
      if (glMap) {
        try {
          glMap.remove();
        } catch (e) {}
        glMap = null;
      }
      if (leafletMap) {
        try {
          leafletMap.remove();
        } catch (e) {}
        leafletMap = null;
      }
    };
  }, [validPoints.length, activeEngine]);

  if (validPoints.length < 2) {
    return (
      <div
        style={{ height }}
        className="w-full rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 flex flex-col items-center justify-center text-gray-400 p-6 text-center"
      >
        <Map className="h-10 w-10 mb-2 text-gray-300 dark:text-gray-600 animate-pulse" />
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Koordinat Rute Belum Tersedia
        </p>
        <p className="text-xs text-gray-400 mt-1 max-w-sm">
          Tidak ditemukan data GPS pelayaran yang cukup (minimal 2 titik koordinat) untuk memvisualisasikan rute peta.
        </p>
      </div>
    );
  }

  return (
    <div className={clsx('relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm', className)}>
      {/* ── TOP FLOATING CONTROL BAR ── */}
      <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between pointer-events-none gap-2 flex-wrap">
        {/* Info Pill */}
        <div className="pointer-events-auto flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 dark:bg-gray-900/90 backdrop-blur border border-gray-200 dark:border-gray-800 shadow-sm text-xs font-medium text-gray-800 dark:text-gray-200">
          <Navigation className="h-3.5 w-3.5 text-brand-500 shrink-0" />
          <span className="font-semibold">{vesselName || 'Vessel Route'}</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-500 dark:text-gray-400 font-mono text-[11px]">
            {validPoints.length} Titik GPS
          </span>
        </div>

        {/* Engine Switcher (Sesuai Konfigurasi User: MapGL / Leaflet) */}
        <div className="pointer-events-auto flex items-center gap-1 p-0.5 rounded-xl bg-white/90 dark:bg-gray-900/90 backdrop-blur border border-gray-200 dark:border-gray-800 shadow-sm">
          <button
            type="button"
            onClick={() => setActiveEngine('mapgl')}
            disabled={!isHardwareGL}
            className={clsx(
              'px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all inline-flex items-center gap-1',
              activeEngine === 'mapgl'
                ? 'bg-brand-500 text-white shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
              !isHardwareGL && 'opacity-40 cursor-not-allowed',
            )}
            title={isHardwareGL ? 'Gunakan Engine MapLibre GL (WebGL)' : 'Perangkat tidak mendukung WebGL'}
          >
            <span>MapGL</span>
            {activeEngine === 'mapgl' && <Check className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => setActiveEngine('leaflet')}
            className={clsx(
              'px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all inline-flex items-center gap-1',
              activeEngine === 'leaflet'
                ? 'bg-brand-500 text-white shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
            )}
            title="Gunakan Engine Leaflet 2D"
          >
            <span>Leaflet</span>
            {activeEngine === 'leaflet' && <Check className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* ── MAP CONTAINER ── */}
      <div ref={containerRef} style={{ height }} className="w-full bg-gray-100 dark:bg-gray-950" />
    </div>
  );
}
